import { useState } from "react";
import {
	Box,
	Typography,
	Paper,
	Stack,
	Radio,
	RadioGroup,
	FormControlLabel,
	IconButton,
	Button,
	Tooltip,
	Divider,
	CircularProgress,
	Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TuneIcon from "@mui/icons-material/Tune";
import ConfirmationModal from "../../../../components/confirmation-modal";
import { StyledTextField } from "../../../../components/ui/CommonReusables";
import { generateButtonSx } from "../../../../utils/elementor-style-override";
import useAgentPreferenceCatalog from "./hooks/useAgentPreferenceCatalog";

const primaryBlue = "#1D54A2";

const ROW_COLUMNS = { xs: "1fr", sm: "1fr 170px 108px" };

const addButtonSx = {
	backgroundColor: `${primaryBlue} !important`,
	color: "#FFFFFF !important",
	fontWeight: 700,
	whiteSpace: "nowrap",
	...generateButtonSx({ bg: "#16407F !important", text: "#FFFFFF" }, { bg: "#16407F !important", text: "#FFFFFF" }),
	"&.Mui-disabled": {
		backgroundColor: "#E3E8EF !important",
		color: "#9AA9BA !important",
	},
};

const saveIconSx = {
	color: "#1E7E4D !important",
	backgroundColor: "#E5F5EC !important",
	...generateButtonSx({ bg: "#CFEBDC !important", text: "#1E7E4D" }, { bg: "#CFEBDC !important", text: "#1E7E4D" }),
};

const cancelIconSx = {
	color: "#4A6A8A !important",
	backgroundColor: "#EBF5FF !important",
	...generateButtonSx({ bg: "#D6E6F5 !important", text: "#1A3F7A" }, { bg: "#D6E6F5 !important", text: "#1A3F7A" }),
};

const editIconSx = {
	color: primaryBlue + " !important",
	backgroundColor: "#EBF5FF !important",
	...generateButtonSx({ bg: "#D6E6F5 !important", text: "#1A3F7A" }, { bg: "#D6E6F5 !important", text: "#1A3F7A" }),
};

const deleteIconSx = {
	color: "#E14B5A !important",
	backgroundColor: "#FFF0F2 !important",
	...generateButtonSx({ bg: "#FBD9DE !important", text: "#B93544" }, { bg: "#FBD9DE !important", text: "#B93544" }),
};

const inputSx = {
	"& .MuiOutlinedInput-input": {
		border: "none !important",
	},
	"&:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: primaryBlue,
	},
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: primaryBlue,
		borderWidth: "1px",
	},
};

const AgentPreferences = ({ showSnackbar }) => {
	const { catalog: preferences, loading, error, createItem, renameItem, toggleItem, deleteItem } = useAgentPreferenceCatalog();
	const [newLabel, setNewLabel] = useState("");
	const [editingId, setEditingId] = useState(null);
	const [draftLabel, setDraftLabel] = useState("");
	const [pendingRename, setPendingRename] = useState(null);
	const [pendingDelete, setPendingDelete] = useState(null);

	const notify = (msg, severity = "info") => {
		if (showSnackbar) showSnackbar(msg, severity);
	};

	const handleAdd = async () => {
		const label = newLabel.trim();
		if (!label) return;

		const result = await createItem(label);
		if (result.success) {
			setNewLabel("");
		} else {
			notify(result.error || "Failed to add preference.", "error");
		}
	};

	const handleToggle = async (id, isEnabled) => {
		const result = await toggleItem(id, isEnabled);
		if (!result.success) {
			notify(result.error || "Failed to update preference availability.", "error");
		}
	};

	const startEditing = (pref) => {
		setEditingId(pref.id);
		setDraftLabel(pref.label);
	};

	const cancelEditing = () => {
		setEditingId(null);
		setDraftLabel("");
	};

	const requestRename = (pref) => {
		const trimmed = draftLabel.trim();
		if (!trimmed || trimmed === pref.label) {
			cancelEditing();
			return;
		}
		setPendingRename({ id: pref.id, oldLabel: pref.label, newLabel: trimmed });
	};

	const confirmRename = async () => {
		if (!pendingRename) return;
		const result = await renameItem(pendingRename.id, pendingRename.newLabel);
		if (!result.success) {
			notify(result.error || "Failed to rename preference.", "error");
		}
		setPendingRename(null);
		cancelEditing();
	};

	const confirmDelete = async () => {
		if (!pendingDelete) return;
		const result = await deleteItem(pendingDelete.id);
		if (!result.success) {
			notify(result.error || "Failed to delete preference.", "error");
		}
		setPendingDelete(null);
	};

	return (
		<Box>
			<Typography variant="h5" fontWeight={600} mb={0.5}>
				Agent Preferences
			</Typography>
			<Typography variant="body2" color="text.secondary" mb={3}>
				Manage the preference options available to agents. Toggle a preference off to hide it without deleting it.
			</Typography>

			<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={3}>
				<StyledTextField
					fullWidth
					size="small"
					placeholder="Add a new preference..."
					value={newLabel}
					onChange={(e) => setNewLabel(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") handleAdd();
					}}
					sx={inputSx}
				/>
				<Button
					variant="contained"
					startIcon={<AddCircleOutlineIcon />}
					onClick={handleAdd}
					disabled={!newLabel.trim()}
					sx={addButtonSx}
				>
					Add
				</Button>
			</Stack>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
					<CircularProgress size={28} sx={{ color: primaryBlue }} />
				</Box>
			) : preferences.length === 0 ? (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						gap: 1,
						py: 6,
						color: "text.secondary",
						border: "1px dashed",
						borderColor: "divider",
						borderRadius: 2,
					}}
				>
					<TuneIcon sx={{ fontSize: 44, color: "#B9C7DC" }} />
					<Typography variant="subtitle1" sx={{ fontWeight: 700, color: primaryBlue }}>
						No preferences yet
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Add one above to get started.
					</Typography>
				</Box>
			) : (
				<Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
					<Box
						sx={{
							display: { xs: "none", sm: "grid" },
							gridTemplateColumns: ROW_COLUMNS.sm,
							alignItems: "center",
							columnGap: 2,
							px: 2,
							py: 1.25,
							bgcolor: "secondary.lightbgcolor",
						}}
					>
						<Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, color: "text.secondary" }}>
							PREFERENCE
						</Typography>
						<Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, color: "text.secondary" }}>
							AVAILABLE
						</Typography>
						<Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, color: "text.secondary", textAlign: "right" }}>
							ACTIONS
						</Typography>
					</Box>
					<Divider sx={{ display: { xs: "none", sm: "block" } }} />

					{preferences.map((pref, index) => {
						const isEditing = editingId === pref.id;

						return (
							<Box key={pref.id}>
								{index > 0 && <Divider />}
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: ROW_COLUMNS,
										alignItems: "center",
										columnGap: 2,
										rowGap: 1.5,
										px: 2,
										py: 1.5,
									}}
								>
									{isEditing ? (
										<StyledTextField
											fullWidth
											size="small"
											value={draftLabel}
											onChange={(e) => setDraftLabel(e.target.value)}
											onKeyDown={(e) => {
												if (e.key === "Enter") requestRename(pref);
												if (e.key === "Escape") cancelEditing();
											}}
											autoFocus
											sx={{ ...inputSx, gridColumn: { xs: "1 / -1", sm: "auto" } }}
										/>
									) : (
										<Typography variant="body2" fontWeight={600}>
											{pref.label}
										</Typography>
									)}

									{!isEditing && (
										<Tooltip title={pref.enabled ? "Available to agents" : "Hidden from agents"}>
											<RadioGroup
												row
												value={pref.enabled ? "yes" : "no"}
												onChange={(e) => handleToggle(pref.id, e.target.value === "yes")}
												sx={{ flexWrap: "nowrap" }}
											>
												<FormControlLabel
													value="yes"
													control={<Radio size="small" sx={{ color: "#B9C7DC", "&.Mui-checked": { color: primaryBlue } }} />}
													label="Yes"
													sx={{ mr: 1, whiteSpace: "nowrap" }}
												/>
												<FormControlLabel
													value="no"
													control={<Radio size="small" sx={{ color: "#B9C7DC", "&.Mui-checked": { color: primaryBlue } }} />}
													label="No"
													sx={{ mr: 0, whiteSpace: "nowrap" }}
												/>
											</RadioGroup>
										</Tooltip>
									)}

									<Stack
										direction="row"
										alignItems="center"
										justifyContent={{ xs: "flex-start", sm: "flex-end" }}
										spacing={0.5}
										sx={{ gridColumn: isEditing ? { xs: "1 / -1", sm: "auto" } : "auto" }}
									>
										{isEditing ? (
											<>
												<Tooltip title="Save">
													<IconButton size="small" onClick={() => requestRename(pref)} sx={saveIconSx}>
														<CheckIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Cancel">
													<IconButton size="small" onClick={cancelEditing} sx={cancelIconSx}>
														<CloseIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</>
										) : (
											<>
												<Tooltip title="Rename">
													<IconButton size="small" onClick={() => startEditing(pref)} sx={editIconSx}>
														<EditIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Delete">
													<IconButton size="small" onClick={() => setPendingDelete(pref)} sx={deleteIconSx}>
														<DeleteOutlineIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</>
										)}
									</Stack>
								</Box>
							</Box>
						);
					})}
				</Paper>
			)}

			<ConfirmationModal
				open={!!pendingRename}
				onClose={() => setPendingRename(null)}
				onConfirm={confirmRename}
				title="Rename Preference?"
				description={pendingRename ? `Rename "${pendingRename.oldLabel}" to "${pendingRename.newLabel}"?` : ""}
				confirmText="Yes, Rename"
				cancelText="Cancel"
			/>

			<ConfirmationModal
				open={!!pendingDelete}
				onClose={() => setPendingDelete(null)}
				onConfirm={confirmDelete}
				title="Delete Preference?"
				description={pendingDelete ? `Are you sure you want to delete "${pendingDelete.label}"?` : ""}
				confirmText="Yes, Delete"
				cancelText="Cancel"
				confirmColor="#E14B5A"
			/>
		</Box>
	);
};

export default AgentPreferences;
